import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { Button, IconButton } from '../ui'
import { ConfirmDialog } from '../ui/Modal/ConfirmDialog.jsx'
import { ReportSelector } from './ReportSelector.jsx'
import { ReportTagModal } from './ReportTagModal.jsx'
import { AccountSection, SecuritySection } from './AccountSecuritySections.jsx'

import { PrepCardTray } from '../cards/preparation/PrepCardTray.jsx'
import { getPrepCard } from '../cards/preparation/prepCardDefinitions.js'
import { api } from '../api/client.js'
import { setClaudeApiKey, deleteClaudeApiKey } from '../api/claude.js'
import { getMode, getTheme, setMode, setTheme } from '../theme.js'

export function SettingsPanel({ user, me, reports, onAddReport, onRemoveReport, onRemoveManager, onUpdateReportTags, onClose, canvasLayout = [], onSaveCanvas, onRequestDeletion, onCancelDeletion }) {
  const [selectedReport, setSelectedReport] = useState(null)
  const [activeTag, setActiveTag] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletionLoading, setDeletionLoading] = useState(false)
  const [showRemoveManagerConfirm, setShowRemoveManagerConfirm] = useState(false)
  const [removeManagerLoading, setRemoveManagerLoading] = useState(false)

  const [canvasEditMode, setCanvasEditMode] = useState(false)
  const [localLayout, setLocalLayout] = useState(canvasLayout)
  const [exportStatus, setExportStatus] = useState(null) // null | 'pending' | 'ready' | 'none'
  const [exportData, setExportData] = useState(null) // { downloadUrl, expiresAt }
  const [exportRequesting, setExportRequesting] = useState(false)
  const pollRef = useRef(null)
  const [dragId, setDragId] = useState(null)
  const [dragOverId, setDragOverId] = useState(null)
  const [canvasSaving, setCanvasSaving] = useState(false)
  const [theme, setThemeState] = useState(() => getTheme())
  const [mode, setModeState] = useState(() => getMode())
  const handleThemeChange = (next) => {
    setTheme(next)
    setThemeState(next)
  }
  const handleModeChange = (next) => {
    setMode(next)
    setModeState(next)
  }

  const [claudeKeyInput, setClaudeKeyInput] = useState('')
  const [claudeKeySaving, setClaudeKeySaving] = useState(false)
  const [claudeKeyError, setClaudeKeyError] = useState(null)

  const hasClaudeKey = me?.hasClaudeKey || false

  const handleSaveClaudeKey = useCallback(async () => {
    if (!claudeKeyInput.trim()) return
    setClaudeKeySaving(true)
    setClaudeKeyError(null)
    try {
      await setClaudeApiKey(claudeKeyInput.trim())
      setClaudeKeyInput('')
      // Refresh me to update hasClaudeKey
      window.location.reload()
    } catch (err) {
      setClaudeKeyError(err.message || 'Failed to save key')
    } finally {
      setClaudeKeySaving(false)
    }
  }, [claudeKeyInput])

  const handleDeleteClaudeKey = useCallback(async () => {
    setClaudeKeySaving(true)
    try {
      await deleteClaudeApiKey()
      window.location.reload()
    } catch (err) {
      setClaudeKeyError(err.message || 'Failed to remove key')
    } finally {
      setClaudeKeySaving(false)
    }
  }, [])

  const graceDays = parseInt(import.meta.env.VITE_DELETION_GRACE_DAYS || '5', 10)

  const handleRequestDeletion = useCallback(async () => {
    setDeletionLoading(true)
    try {
      await onRequestDeletion()
      setShowDeleteConfirm(false)
    } finally {
      setDeletionLoading(false)
    }
  }, [onRequestDeletion])

  const handleCancelDeletion = useCallback(async () => {
    setDeletionLoading(true)
    try {
      await onCancelDeletion()
    } finally {
      setDeletionLoading(false)
    }
  }, [onCancelDeletion])

  const handleRemoveManager = useCallback(async () => {
    setRemoveManagerLoading(true)
    try {
      await onRemoveManager()
      setShowRemoveManagerConfirm(false)
    } finally {
      setRemoveManagerLoading(false)
    }
  }, [onRemoveManager])

  const allTags = useMemo(() => {
    const tags = new Set()
    reports.forEach((r) => (r.tags || []).forEach((t) => tags.add(t)))
    return [...tags].sort()
  }, [reports])

  const filteredReports = useMemo(() => {
    if (!activeTag) return reports
    return reports.filter((r) => (r.tags || []).includes(activeTag))
  }, [reports, activeTag])

  const fetchExportStatus = useCallback(async () => {
    try {
      const data = await api.getExport()
      setExportStatus(data.status)
      if (data.status === 'ready') {
        setExportData({ downloadUrl: data.downloadUrl, expiresAt: data.expiresAt })
      }
      return data.status
    } catch (_) {
      return null
    }
  }, [])

  useEffect(() => {
    fetchExportStatus()
  }, [fetchExportStatus])

  useEffect(() => {
    if (exportStatus === 'pending') {
      pollRef.current = setInterval(async () => {
        const status = await fetchExportStatus()
        if (status !== 'pending') clearInterval(pollRef.current)
      }, 3000)
    } else {
      clearInterval(pollRef.current)
    }
    return () => clearInterval(pollRef.current)
  }, [exportStatus, fetchExportStatus])

  async function handleRequestExport() {
    setExportRequesting(true)
    try {
      await api.requestExport()
      setExportStatus('pending')
      setExportData(null)
    } finally {
      setExportRequesting(false)
    }
  }

  function handleEditCanvas() {
    setLocalLayout(canvasLayout)
    setCanvasEditMode(true)
  }

  function handleCancelEdit() {
    setLocalLayout(canvasLayout)
    setCanvasEditMode(false)
  }

  async function handleSaveCanvas() {
    setCanvasSaving(true)
    try {
      await onSaveCanvas(localLayout)
      setCanvasEditMode(false)
    } finally {
      setCanvasSaving(false)
    }
  }

  function handleRemoveCard(id) {
    setLocalLayout((prev) => prev.filter((cid) => cid !== id))
  }

  function handleAddCard(id) {
    setLocalLayout((prev) => [...prev, id])
  }

  function handleDragStart(e, id) {
    setDragId(id)
    e.dataTransfer.effectAllowed = 'move'
  }

  function handleDragOver(e, id) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (id !== dragId) setDragOverId(id)
  }

  function handleDrop(e, id) {
    e.preventDefault()
    if (!dragId || dragId === id) return
    setLocalLayout((prev) => {
      const next = [...prev]
      const from = next.indexOf(dragId)
      const to = next.indexOf(id)
      if (from === -1 || to === -1) return prev
      next.splice(from, 1)
      next.splice(to, 0, dragId)
      return next
    })
    setDragId(null)
    setDragOverId(null)
  }

  function handleDragEnd() {
    setDragId(null)
    setDragOverId(null)
  }

  const displayLayout = canvasEditMode ? localLayout : canvasLayout

  const settingsContent = (
    <div style={styles.outer}>
      <div style={styles.inner}>
        <div style={styles.header}>
          <h2 style={styles.title}>Settings</h2>
          <IconButton ariaLabel="Close settings" onClick={onClose}>×</IconButton>
        </div>

        <AccountSection user={user} />

        <SecuritySection user={user} />

        {me?.managerId && (
          <section style={styles.section}>
            <p style={styles.sectionLabel}>Manager</p>
            <div style={styles.card}>
              <div style={styles.field}>
                <span style={styles.fieldLabel}>Name</span>
                <span style={styles.fieldValue}>{me.managerName || '—'}</span>
              </div>
              <div style={styles.field}>
                <span style={styles.fieldLabel}>Email</span>
                <span style={styles.fieldValue}>{me.managerEmail || '—'}</span>
              </div>
              <div style={styles.cardFooter}>
                <Button variant="secondary" size="sm" onClick={() => setShowRemoveManagerConfirm(true)}>
                  Remove manager
                </Button>
              </div>
            </div>
          </section>
        )}

        <section style={styles.section}>
          <p style={styles.sectionLabel}>My canvas</p>
          <div style={styles.card}>
            <p style={styles.canvasNote}>
              Edit your canvas to change the default for new sessions
            </p>
            <p style={styles.canvasCount}>{displayLayout.length} cards</p>
            <div style={styles.canvasChips}>
              {displayLayout.map((id) => {
                const card = getPrepCard(id)
                if (!card) return null
                const isDragging = canvasEditMode && dragId === id
                const isDragOver = canvasEditMode && dragOverId === id
                return (
                  <span
                    key={id}
                    draggable={canvasEditMode}
                    onDragStart={canvasEditMode ? (e) => handleDragStart(e, id) : undefined}
                    onDragOver={canvasEditMode ? (e) => handleDragOver(e, id) : undefined}
                    onDrop={canvasEditMode ? (e) => handleDrop(e, id) : undefined}
                    onDragEnd={canvasEditMode ? handleDragEnd : undefined}
                    style={{
                      ...styles.canvasChip,
                      opacity: isDragging ? 0.4 : 1,
                      cursor: canvasEditMode ? 'grab' : 'default',
                      outline: isDragOver ? '2px dashed var(--text-secondary)' : 'none',
                    }}
                  >
                    <span>{card.label}</span>
                    {canvasEditMode && (
                      <IconButton
                        ariaLabel={`Remove ${card.label}`}
                        onClick={() => handleRemoveCard(id)}
                      >
                        ×
                      </IconButton>
                    )}
                  </span>
                )
              })}
            </div>
            {canvasEditMode && (
              <PrepCardTray layout={localLayout} onAddCard={handleAddCard} />
            )}
            <div style={styles.cardFooter}>
              {canvasEditMode ? (
                <div style={styles.canvasEditActions}>
                  <Button variant="secondary" size="sm" onClick={handleCancelEdit}>Cancel</Button>
                  <Button size="sm" onClick={handleSaveCanvas} disabled={canvasSaving}>
                    {canvasSaving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              ) : (
                <Button variant="secondary" size="sm" onClick={handleEditCanvas}>Edit</Button>
              )}
            </div>
          </div>
        </section>

        <section style={styles.section}>
          <p style={styles.sectionLabel}>Team management</p>
          {allTags.length > 0 && (
            <div style={styles.tagFilter}>
              <button
                style={{ ...styles.tagPill, ...(activeTag === null ? styles.tagPillActive : {}) }}
                onClick={() => setActiveTag(null)}
              >All</button>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  style={{ ...styles.tagPill, ...(activeTag === tag ? styles.tagPillActive : {}) }}
                  onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                >{tag}</button>
              ))}
            </div>
          )}
          <div style={styles.card}>
            <ReportSelector
              reports={filteredReports}
              selectedReportId={null}
              onSelectReport={() => {}}
              onAddReport={onAddReport}
              onRemoveReport={onRemoveReport}
              onClickReport={(r) => r.status !== 'pending' && setSelectedReport(r)}
              showRemove
              renderBadge={(r) =>
                r.status === 'pending' ? (
                  <span style={styles.pendingBadge}>Pending</span>
                ) : null
              }
            />
          </div>
        </section>
        <section style={styles.section}>
          <p style={styles.sectionLabel}>Appearance (preview)</p>
          <div style={styles.card}>
            <p style={styles.canvasNote}>
              Try the new warm theme. This is a preview — your choice is saved on this device only.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                { value: 'default', label: 'Default' },
                { value: 'warm', label: 'Warm' },
                { value: 'forest', label: 'Forest' },
                { value: 'ocean', label: 'Ocean' },
              ].map((opt) => {
                const active = theme === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleThemeChange(opt.value)}
                    style={{
                      padding: '6px 14px',
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: 999,
                      border: '1px solid var(--border)',
                      background: active ? 'var(--accent-brand-bg)' : 'transparent',
                      color: active ? 'var(--accent-brand-text)' : 'var(--text-secondary)',
                      cursor: 'pointer',
                    }}
                    aria-pressed={active}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
            <p style={{ ...styles.canvasNote, marginTop: 12 }}>
              Mode: light, dark, or follow your system.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { value: 'light', label: 'Light' },
                { value: 'dark', label: 'Dark' },
                { value: 'system', label: 'System' },
              ].map((opt) => {
                const active = mode === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleModeChange(opt.value)}
                    style={{
                      padding: '6px 14px',
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: 999,
                      border: '1px solid var(--border)',
                      background: active ? 'var(--accent-brand-bg)' : 'transparent',
                      color: active ? 'var(--accent-brand-text)' : 'var(--text-secondary)',
                      cursor: 'pointer',
                    }}
                    aria-pressed={active}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>
        </section>
        <section style={styles.section}>
          <p style={styles.sectionLabel}>AI assistant</p>
          <div style={styles.card}>
            <p style={styles.canvasNote}>
              Connect your Anthropic API key to get AI-suggested coaching notes on reflections.
              Your key is stored securely and never shared.
            </p>
            {hasClaudeKey ? (
              <div>
                <div style={styles.field}>
                  <span style={styles.fieldLabel}>API key</span>
                  <span style={styles.fieldValue}>sk-ant-···configured</span>
                </div>
                <div style={styles.cardFooter}>
                  <Button variant="secondary" size="sm" onClick={handleDeleteClaudeKey} disabled={claudeKeySaving}>
                    {claudeKeySaving ? 'Removing…' : 'Remove key'}
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="password"
                    placeholder="sk-ant-..."
                    value={claudeKeyInput}
                    onChange={(e) => setClaudeKeyInput(e.target.value)}
                    style={styles.passwordInput}
                  />
                  <Button size="sm" onClick={handleSaveClaudeKey} disabled={claudeKeySaving || !claudeKeyInput.trim()}>
                    {claudeKeySaving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
                {claudeKeyError && (
                  <p style={{ fontSize: '11px', color: 'var(--danger-text)', margin: '6px 0 0' }}>{claudeKeyError}</p>
                )}
              </div>
            )}
          </div>
        </section>

        <section style={styles.section}>
          <p style={styles.sectionLabel}>Data &amp; privacy</p>
          <div style={styles.card}>
            <p style={styles.canvasNote}>
              Download a copy of all data Soundcheck holds about you.
            </p>
            <div style={styles.cardFooter}>
              {exportStatus === 'pending' && (
                <span style={styles.exportPending}>Preparing your export…</span>
              )}
              {exportStatus === 'ready' && exportData && (
                <div style={styles.exportReady}>
                  <a href={exportData.downloadUrl} download style={styles.downloadBtn}>
                    Download your data
                  </a>
                  <span style={styles.exportExpiry}>
                    Expires {new Date(exportData.expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              )}
              {(exportStatus === 'none' || exportStatus === null) && (
                <Button size="sm" onClick={handleRequestExport} disabled={exportRequesting}>
                  {exportRequesting ? 'Requesting…' : 'Request data export'}
                </Button>
              )}
              {exportStatus === 'ready' && (
                <button style={styles.requestNewExportBtn} onClick={handleRequestExport} disabled={exportRequesting}>
                  Request new export
                </button>
              )}
            </div>
          </div>
        </section>

        <section style={styles.section}>
          <p style={{ ...styles.sectionLabel, color: 'var(--danger-text)' }}>Danger zone</p>
          <div style={{ ...styles.card, borderColor: 'var(--danger-text)' }}>
            {me?.deletionRequest ? (
              <div>
                <p style={styles.dangerText}>
                  Account scheduled for deletion on{' '}
                  <strong>
                    {new Date(me.deletionRequest.scheduledFor).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </strong>.
                </p>
                <button
                  style={styles.cancelDeletionBtn}
                  onClick={handleCancelDeletion}
                  disabled={deletionLoading}
                >
                  {deletionLoading ? 'Cancelling...' : 'Cancel deletion'}
                </button>
              </div>
            ) : (
              <div>
                <p style={styles.dangerText}>
                  Permanently delete your account and all associated data.
                </p>
                <button
                  style={styles.requestDeletionBtn}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Request account deletion
                </button>
              </div>
            )}
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={showRemoveManagerConfirm}
        title="Remove manager?"
        message={`${me?.managerName || 'Your manager'} will no longer have access to your sessions.`}
        confirmLabel={removeManagerLoading ? 'Removing...' : 'Remove manager'}
        onConfirm={handleRemoveManager}
        onCancel={() => setShowRemoveManagerConfirm(false)}
        tone="danger"
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete your account?"
        message={`All your sessions and data will be permanently deleted. You have ${graceDays} days to cancel this request.`}
        confirmLabel={deletionLoading ? 'Requesting...' : 'Delete my account'}
        onConfirm={handleRequestDeletion}
        onCancel={() => setShowDeleteConfirm(false)}
        tone="danger"
      />

      {selectedReport && (
        <ReportTagModal
          report={selectedReport}
          onUpdateTags={async (reportId, tags) => {
            await onUpdateReportTags(reportId, tags)
            setSelectedReport((r) => r ? { ...r, tags } : null)
          }}
          onClose={() => setSelectedReport(null)}
        />
      )}

    </div>
  )

  return settingsContent
}

const styles = {
  outer: {
    flex: 1,
    overflowY: 'auto',
    background: 'var(--bg)',
  },
  editWrapper: {
    display: 'flex',
    height: '100%',
    overflow: 'hidden',
  },
  editMain: {
    flex: 1,
    overflowY: 'auto',
  },
  inner: {
    maxWidth: '640px',
    margin: '0 auto',
    padding: '32px 24px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '28px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: 'var(--text-primary)',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    color: 'var(--text-secondary)',
    padding: '4px 6px',
    borderRadius: '4px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px',
  },
  tagFilter: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' },
  tagPill: {
    fontSize: '11px', padding: '4px 10px', borderRadius: '10px', border: '1px solid var(--border)',
    background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
  },
  tagPillActive: { background: 'var(--text-primary)', color: '#fff', borderColor: 'var(--text-primary)' },
  card: {
    background: 'var(--surface, var(--sidebar-bg))',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    padding: '16px',
  },
  field: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: '1px solid var(--border)',
    marginBottom: '4px',
  },
  fieldLabel: { fontSize: '12px', color: 'var(--text-secondary)', flex: 1, minWidth: 0, wordBreak: 'break-word' },
  fieldValue: { fontSize: '13px', color: 'var(--text-primary)', flexShrink: 0, textAlign: 'right' },
  cardFooter: { marginTop: '12px' },
  changePasswordBtn: {
    padding: '6px 12px',
    fontSize: '12px',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    background: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
  },
  canvasEditActions: {
    display: 'flex',
    gap: '6px',
  },
  saveCanvasBtn: {
    padding: '4px 10px',
    fontSize: '11px',
    border: 'none',
    borderRadius: '4px',
    background: 'var(--text-primary)',
    color: '#fff',
    fontWeight: 600,
    cursor: 'pointer',
  },
  canvasNote: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    margin: '0 0 8px 0',
  },
  canvasCount: {
    fontSize: '11px',
    color: 'var(--text-secondary)',
    margin: '0 0 10px 0',
    fontWeight: 600,
  },
  canvasChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  canvasChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 8px',
    borderRadius: '12px',
    border: '1px solid var(--border)',
    fontSize: '11px',
    color: 'var(--text-primary)',
    transition: 'opacity 0.15s',
  },
  chipRemoveBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
    fontSize: '14px',
    lineHeight: 1,
    padding: '0 0 0 2px',
    display: 'flex',
    alignItems: 'center',
  },
  pendingBadge: {
    display: 'inline-block',
    fontSize: '10px',
    fontWeight: 600,
    padding: '2px 6px',
    borderRadius: '10px',
    background: 'var(--info-bg)',
    color: 'var(--info-text)',
    marginTop: '2px',
  },
  dangerText: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    margin: '0 0 12px 0',
  },
  requestDeletionBtn: {
    padding: '6px 12px',
    fontSize: '12px',
    border: '1px solid var(--danger-text)',
    borderRadius: '4px',
    background: 'none',
    color: 'var(--danger-text)',
    cursor: 'pointer',
  },
  cancelDeletionBtn: {
    padding: '6px 12px',
    fontSize: '12px',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    background: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
  },
  exportPending: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    fontStyle: 'italic',
  },
  exportReady: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  downloadBtn: {
    padding: '6px 12px',
    fontSize: '12px',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    background: 'var(--text-primary)',
    color: '#fff',
    cursor: 'pointer',
    fontWeight: 600,
    textDecoration: 'none',
    display: 'inline-block',
  },
  exportExpiry: {
    fontSize: '11px',
    color: 'var(--text-secondary)',
  },
  totpDivider: {
    borderTop: '1px solid var(--border)',
    margin: '12px 0',
  },
  totpSetup: {
    marginTop: '12px',
  },
  totpInstructions: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    margin: '0 0 10px 0',
  },
  totpQrWrap: {
    display: 'flex',
    justifyContent: 'center',
    margin: '0 0 10px 0',
  },
  totpQrImg: {
    borderRadius: '6px',
    border: '1px solid var(--border)',
  },
  totpSecretRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '12px',
  },
  totpSecretCode: {
    fontSize: '12px',
    fontFamily: 'monospace',
    letterSpacing: '0.05em',
    color: 'var(--text-secondary)',
    background: 'var(--bg)',
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid var(--border)',
    userSelect: 'all',
    wordBreak: 'break-all',
  },
  totpVerifyRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  totpInput: {
    width: '100px',
    padding: '6px 10px',
    fontSize: '14px',
    fontFamily: 'monospace',
    letterSpacing: '0.15em',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    background: 'var(--bg)',
    color: 'var(--text-primary)',
    textAlign: 'center',
  },
  totpCancelBtn: {
    marginTop: '8px',
    padding: '0',
    fontSize: '11px',
    border: 'none',
    background: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  securityCheckRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 0',
    borderBottom: '1px solid var(--border)',
    marginBottom: '4px',
  },
  securityCheckIcon: {
    fontSize: '13px',
    color: 'var(--success-text)',
    fontWeight: 700,
    width: '16px',
    textAlign: 'center',
    flexShrink: 0,
  },
  securityCheckIconWarn: {
    fontSize: '13px',
    color: 'var(--info-text)',
    fontWeight: 700,
    width: '16px',
    textAlign: 'center',
    flexShrink: 0,
  },
  securityCheckLabel: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-word',
  },
  securityCheckLabelWarn: {
    fontSize: '12px',
    color: 'var(--info-text)',
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-word',
  },
  securityValueWarn: {
    fontSize: '13px',
    color: 'var(--info-text)',
    flexShrink: 0,
    textAlign: 'right',
  },
  securityActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  dangerBtn: {
    padding: '6px 12px',
    fontSize: '12px',
    border: '1px solid var(--danger-text)',
    borderRadius: '4px',
    background: 'none',
    color: 'var(--danger-text)',
    cursor: 'pointer',
  },
  passwordInput: {
    padding: '6px 10px',
    fontSize: '13px',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    background: 'var(--bg)',
    color: 'var(--text-primary)',
    width: '100%',
    boxSizing: 'border-box',
  },
  requestNewExportBtn: {
    marginTop: '6px',
    padding: '0',
    fontSize: '11px',
    border: 'none',
    background: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
}
